// TNCCF/internal-staff auth storage — deliberately separate keys from
// Utils/supplierAuth.js. An admin token (signed with ADMIN_JWT_SECRET on
// the backend) must never be confused with a supplier session, or vice
// versa — mirrors the backend's own token isolation.

export const setAdminAuthData = (token, user) => {
  localStorage.setItem("admin_token", token);
  localStorage.setItem("admin_info", JSON.stringify(user));
  localStorage.setItem("admin_isAuthenticated", "true");
};

export const clearAdminAuthData = () => {
  localStorage.removeItem("admin_token");
  localStorage.removeItem("admin_info");
  localStorage.removeItem("admin_isAuthenticated");
};

export const getAdminAuthData = () => {
  const token = localStorage.getItem("admin_token");
  const infoRaw = localStorage.getItem("admin_info");
  return {
    token,
    user: infoRaw ? JSON.parse(infoRaw) : null,
  };
};

export const isAdminAuthenticated = () => {
  return Boolean(
    localStorage.getItem("admin_token") &&
      localStorage.getItem("admin_isAuthenticated")
  );
};

// Reviewer roles per jpc-supplier-backend/middleware/procurement/jpcUserAuth.js —
// Super Admin(1) / Admin(2) / TNCCF Office(5) / TNCCF Head(7, added
// 2026-07-24). Used to hide/show the supplier-review nav for roles that
// can log in but aren't reviewers (CWS Admin/CWS User) — the backend
// still enforces this regardless.
export const REVIEWER_ROLE_IDS = [1, 2, 5, 7];

// Price-viewer roles (added 2026-07-24, Workstream A) — ONLY TNCCF Head
// may see submitted tender prices or the cascading-award console, not
// even Super Admin/Admin/TNCCF Office. Mirrors PRICE_VIEWER_ROLE_IDS in
// jpc-supplier-backend/middleware/procurement/jpcUserAuth.js.
export const PRICE_VIEWER_ROLE_IDS = [7];

// Quality Check Committee (role_id 8, added 2026-07-29) — the only role
// that submits an Eye Sight Report (photo + remarks) against a bidding
// supplier's product. Mirrors QUALITY_CHECK_ROLE_IDS in
// jpc-supplier-backend/middleware/procurement/jpcUserAuth.js.
export const QUALITY_CHECK_ROLE_IDS = [8];

// Rate Fixing Committee (role_id 10, added 2026-07-29) — records a
// product's market price with a date; TNCCF Head sees the latest value
// on Price Review as a reference (display only). Mirrors
// RATE_FIXING_COMMITTEE_ROLE_IDS in jpc-supplier-backend/middleware/
// procurement/jpcUserAuth.js.
export const RATE_FIXING_ROLE_IDS = [10];

// RCS (role_id 11, added 2026-08-07) — its own dashboard (products
// count + a link into Rate Fixing Committee records), read-only view of
// the Rate Fixing Committee's submissions, and the same read-only
// Products list CWS uses. Deliberately NOT part of PRICE_VIEWER_ROLE_IDS
// (no supplier-bid prices, no second OTP step-up) or REVIEWER_ROLE_IDS
// (no supplier-review powers) — mirrors RCS_ROLE_IDS in jpc-supplier-
// backend/middleware/procurement/jpcUserAuth.js.
export const RCS_ROLE_IDS = [11];

// Product List management (added 2026-07-29) — Super Admin/Admin/
// TNCCF Office/TNCCF Head (client confirmed Head should see/manage this
// too, same day it was built). Mirrors SUPPLIER_LIST_MANAGER_ROLE_IDS
// in jpc-supplier-backend/middleware/procurement/jpcUserAuth.js (name
// kept as-is on the backend; only the frontend-facing feature/file/
// table names were renamed to "Product List").
export const PRODUCT_LIST_MANAGER_ROLE_IDS = [1, 2, 5, 7];

// CWS Admin(3) / CWS User(4) — submit purchase indents. Mirrors
// CWS_ROLE_IDS in jpc-supplier-backend/controller/procurement/jpc_indent.js.
export const CWS_ROLE_IDS = [3, 4];

// Marketing Federation Admin(12) / Marketing Federation User(13) — same
// use case as CWS (submit/manage purchase indents for their own
// company). Kept as its own sibling constant, not merged into
// CWS_ROLE_IDS, mirroring jpc_indent.js's own admin/user asymmetry
// checks. Mirrors MF_ROLE_IDS in jpc-supplier-backend/middleware/
// procurement/jpcUserAuth.js.
export const MF_ROLE_IDS = [12, 13];

// Reports page (Indent Report + Supply Order Report, added 2026-08-15)
// — CWS (own company only, auto-scoped server-side) plus every TNCCF
// reviewer role plus RCS (cross-company, read-only). Mirrors
// REPORT_VIEWER_ROLE_IDS in jpc-supplier-backend/middleware/procurement/
// jpcUserAuth.js. No pricing/rate figures in this report for any role,
// so PRICE_VIEWER_ROLE_IDS isn't folded in here.
export const REPORT_VIEWER_ROLE_IDS = [...CWS_ROLE_IDS, ...MF_ROLE_IDS, ...REVIEWER_ROLE_IDS, ...RCS_ROLE_IDS];

// CWS Admin ONLY (added 2026-08-13) — manages their own company's CWS
// User sub-accounts and approves/rejects their company's indents before
// they become visible to TNCCF. Mirrors the CWS_ADMIN_ROLE_IDS literal
// in jpc-supplier-backend/controller/procurement/cws_user_management.js
// (there, `requireRole([3])`).
export const CWS_ADMIN_ROLE_IDS = [3];

// Marketing Federation Admin ONLY — manages their own company's
// Marketing Federation User sub-accounts and approves/rejects their
// company's indents, mirroring CWS_ADMIN_ROLE_IDS. Mirrors the
// MF_ADMIN_ROLE_IDS literal in jpc-supplier-backend/controller/
// procurement/mf_user_management.js (there, `requireRole([12])`).
export const MF_ADMIN_ROLE_IDS = [12];

// CWS company master's display_name edit (added 2026-08-04) — TNCCF
// Admin(5)/TNCCF Head(7) only, narrower than REVIEWER_ROLE_IDS. Mirrors
// COMPANY_EDITOR_ROLE_IDS in jpc-supplier-backend/middleware/procurement/
// jpcUserAuth.js.
export const COMPANY_EDITOR_ROLE_IDS = [5, 7];

// Mismatch-report review + replacement-notice decisions (v3, 2026-08-07)
// — TNCCF Office(5) OR TNCCF Head(7), widened from PRICE_VIEWER_ROLE_IDS
// (Head-only). Mirrors MISMATCH_REVIEWER_ROLE_IDS in jpc-supplier-backend/
// middleware/procurement/jpcUserAuth.js.
export const MISMATCH_REVIEWER_ROLE_IDS = [5, 7];

// Read-only Replacement Notices history for RCS (added per client ask:
// "they can see the notices history only view not to take any
// action"). Mirrors REPLACEMENT_NOTICE_VIEWER_ROLE_IDS in jpc-supplier-
// backend/middleware/procurement/jpcUserAuth.js — GET-only, no decide
// action anywhere on this role's own view.
export const REPLACEMENT_NOTICE_VIEWER_ROLE_IDS = [...MISMATCH_REVIEWER_ROLE_IDS, ...RCS_ROLE_IDS];

// Read-only oversight of CMS purchase-source declarations for TNCCF
// Head and RCS (added per client ask: "this updates from the cms need
// to be shown to the tnccf head and the rcs"). Mirrors
// CMS_DECLARATION_VIEWER_ROLE_IDS in jpc-supplier-backend/middleware/
// procurement/jpcUserAuth.js.
export const CMS_DECLARATION_VIEWER_ROLE_IDS = [...PRICE_VIEWER_ROLE_IDS, ...RCS_ROLE_IDS];

// RCS view-only widening (added 2026-08-16, client ask: RCS should see
// several TNCCF-only pages read-only, with zero write/action capability
// anywhere — RCS has always been strictly read-only, see RCS_ROLE_IDS's
// own comment above). Mirrors PROCUREMENT_VIEWER_ROLE_IDS in jpc-
// supplier-backend/middleware/procurement/jpcUserAuth.js — used for the
// Indent Submitted, Product Demand, Registered Participants, Supply
// Monitoring, and CWS Stores nav items/routes. Every write/action button
// on these pages must still check REVIEWER_ROLE_IDS (or a narrower
// constant) directly, not this one.
export const PROCUREMENT_VIEWER_ROLE_IDS = [...REVIEWER_ROLE_IDS, ...RCS_ROLE_IDS];

// Procurement (tenders) nav item/route — mirrors TENDER_VIEWER_ROLE_IDS
// in jpc-supplier-backend/middleware/procurement/jpcUserAuth.js. Every
// action on TenderDetail.jsx (approve/close/cancel tender, award/
// negotiate/allocate/assign-stores) must still gate on REVIEWER_ROLE_IDS/
// PRICE_VIEWER_ROLE_IDS directly, not this one.
export const TENDER_VIEWER_ROLE_IDS = [...REVIEWER_ROLE_IDS, ...RCS_ROLE_IDS];

// Mismatch Reports LISTING only — mirrors MISMATCH_REPORT_VIEWER_ROLE_IDS
// in jpc-supplier-backend/middleware/procurement/jpcUserAuth.js. The
// decide/review action stays MISMATCH_REVIEWER_ROLE_IDS-only.
export const MISMATCH_REPORT_VIEWER_ROLE_IDS = [...MISMATCH_REVIEWER_ROLE_IDS, ...RCS_ROLE_IDS];

// Branded Vendors & Direct Purchase Orders (2026-09-27) — mirrors
// jpcUserAuth.js: TNCCF Office + Head manage vendors/prices and create/
// cancel/resend POs; Super Admin, Admin and RCS can only view.
export const BRANDED_VENDOR_MANAGER_ROLE_IDS = [5, 7];
export const BRANDED_VENDOR_VIEWER_ROLE_IDS = [1, 2, 5, 7, ...RCS_ROLE_IDS];

// Manual Supply Orders (2026-10-02) — mirrors jpcUserAuth.js: TNCCF Office
// + Head create / resend / cancel; Super Admin, Admin and RCS view the history.
export const MANUAL_SUPPLY_ORDER_MANAGER_ROLE_IDS = [5, 7];
export const MANUAL_SUPPLY_ORDER_VIEWER_ROLE_IDS = [1, 2, 5, 7, ...RCS_ROLE_IDS];

// FMCG catalogue + FMCG indents (2026-10-07) — mirrors controller/procurement/
// fmcg.js: TNCCF Office + Head edit the FMCG product master; Super Admin,
// Admin and RCS view it and every store's FMCG indents; stores (CWS / MF)
// raise their own monthly FMCG indent.
export const FMCG_MANAGER_ROLE_IDS = [5, 7];
export const FMCG_VIEWER_ROLE_IDS = [1, 2, 5, 7, ...RCS_ROLE_IDS];
export const FMCG_STORE_ROLE_IDS = [...CWS_ROLE_IDS, ...MF_ROLE_IDS];

// Mirrors mst_jpc_role's seeded rows (006_jpc_user_role_schema.sql,
// 037_quality_check_committee_and_eyesight_report.sql) — the login
// response only returns role_id, not role_name, so the header's portal
// title is derived from this map rather than a round trip to the DB
// for a label that never changes.
export const ROLE_NAMES = {
  1: "Super Admin",
  2: "Admin",
  3: "CWS Admin",
  4: "CWS User",
  5: "TNCCF Office",
  6: "Supplier",
  7: "TNCCF Head",
  8: "Quality Check Committee",
  9: "Grievance Redressal",
  10: "Rate Fixing Committee",
  11: "RCS",
  12: "Marketing Federation Admin",
  13: "Marketing Federation User",
};

// Grievance Redressal (role_id 9) — no real page built yet, lands on
// the "Coming Soon" placeholder (App.js's ComingSoonPage).
export const GRIEVANCE_ROLE_IDS = [9];

// Single source of truth for "which page does this role_id land on
// right after login" — mirrors App.js's own adminHomePath() (kept as a
// dependency-free duplicate there for its route-guard logic), used here
// by Login.jsx so a fresh login and an already-authenticated redirect
// never disagree about a role's home page again.
export const adminHomePath = (roleId) => {
  if (QUALITY_CHECK_ROLE_IDS.includes(roleId)) return "/admin/quality-check";
  if (RATE_FIXING_ROLE_IDS.includes(roleId)) return "/admin/rate-fixing";
  if (RCS_ROLE_IDS.includes(roleId)) return "/admin/rcs-dashboard";
  if (GRIEVANCE_ROLE_IDS.includes(roleId)) return "/admin/coming-soon";
  if (REVIEWER_ROLE_IDS.includes(roleId) || CWS_ROLE_IDS.includes(roleId) || MF_ROLE_IDS.includes(roleId)) return "/admin/dashboard";
  return "/admin/login";
};
